import type { WikiMarker } from "$lib/api/wiki/response.types";

export const BANNED_WIKI_ARTICLE_TITLE_KEYWORDS: string[] = ['station', 'railway', 'metro', 'airport', 'subway', 'radio station', 'list of'];
export type WikiImageSize = 20 | 40 | 60 | 120 | 250 | 330 | 500 | 960;

export async function wikiGeoSearch({
	centre,
	bannedWords = BANNED_WIKI_ARTICLE_TITLE_KEYWORDS,
	cachedWikiItemTitles,
}: {
	centre: { lat: number, lon: number },
	bannedWords?: string[],
	cachedWikiItemTitles: Set<string>
}): Promise<WikiMarker[]> {
	const radiusMeters = 700;
	const searchUrl =
		`https://en.wikipedia.org/w/api.php` +
		`?action=query` +
		`&list=geosearch` +
		`&gsprimary=all` +
		`&gscoord=${centre.lat}|${centre.lon}` +
		`&gsradius=${radiusMeters}` +
		`&gslimit=500` +
		`&format=json` +
		`&gsnamespace=0` +
		`&origin=*`;

	let searchRes: Response;
	try {
		searchRes = await fetch(searchUrl);
	} catch (err: any) {
		throw new Error(`Wikipedia Geosearch network request failed. URL: [${searchUrl}]. Reason: ${err.message || err}`);
	}
	if (!searchRes.ok) {
		throw new Error(`[Status ${searchRes.status}] Wikipedia Geosearch API rejected: ${searchRes.statusText}. URL: [${searchUrl}]`);
	}

	if (!searchRes.ok) throw new Error(`Geosearch failed: ${searchRes.statusText}`);

	let searchData: WikiGeosearchResponse;
	try {
		searchData = (await searchRes.json()) as WikiGeosearchResponse;
	} catch (err: any) {
		throw new Error(`Failed to parse Wikipedia Geosearch JSON payload. Reason: ${err.message || err}`);
	}

	const geoResults: { pageid: number; title: string; lat: number; lon: number }[] =
		searchData.query?.geosearch ?? [];

	if (geoResults.length === 0) return [];

	const filtered = geoResults.filter(
		({ title }) =>
			!cachedWikiItemTitles.has(title) &&
			!bannedWords.some((word) => title.toLowerCase().includes(word))
	);

	if (filtered.length === 0) return [];
	const imagePages = await fetchWikiImages(filtered);
	return filtered
		.filter(({ pageid }) => imagePages[pageid]?.thumbnail?.source != null)
		.map(({ title, lat, lon, pageid }) => ({
			title,
			coordinates: { latitude: lat, longitude: lon },
			image: imagePages[pageid]!.thumbnail!.source,
		}));
}

async function fetchWikiImages(filtered: WikiGeosearchResult[]): Promise<Record<string, WikiImagePage>> {
	const MAX_WIKI_BATCH_SIZE = 50;
	const imagePages: Record<string, WikiImagePage> = {};

	for (let i = 0; i < filtered.length; i += MAX_WIKI_BATCH_SIZE) {
		const batch = filtered.slice(i, i + MAX_WIKI_BATCH_SIZE);
		const pageIds = batch.map((p) => p.pageid).join('|');

		const imageUrl =
			`https://en.wikipedia.org/w/api.php` +
			`?action=query` +
			`&pageids=${pageIds}` +
			`&prop=pageimages` +
			`&piprop=thumbnail` +
			`&pithumbsize=120` +
			`&pilimit=50` +
			`&format=json` +
			`&origin=*`;

		let imageRes: Response;
		try {
			imageRes = await fetch(imageUrl);
		} catch (err: any) {
			throw new Error(`Wikipedia Image Fetch network request failed. PageIds: [${pageIds}]. Reason: ${err.message || err}`);
		}

		if (!imageRes.ok) {
			console.error(`Failed to fetch Wikipedia image for '${imageUrl}': ${imageRes.statusText}`);
			throw new Error(`Image fetch failed: ${imageRes.statusText}`);
		}

		let imageData: WikiImageResponse;
		try {
			imageData = (await imageRes.json()) as WikiImageResponse;
		} catch (err: any) {
			throw new Error(`Failed to parse Wikipedia Image JSON payload. PageIds: [${pageIds}]. Reason: ${err.message || err}`);
		}

		Object.assign(imagePages, imageData.query?.pages ?? {});
	}

	return imagePages;
}

export async function wikiGetWikiDetails(
	title: string,
	{ maxImages = 6, imageSize = 500 }: { maxImages?: number; imageSize?: WikiImageSize } = {}
): Promise<WikiItemDetails> {
	const [summaryRes, mediaRes] = await Promise.all([
		fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`),
		fetch(`https://en.wikipedia.org/api/rest_v1/page/media-list/${encodeURIComponent(title)}`)
	]);

	if (!summaryRes.ok) {
		console.error(`Failed to fetch Wikipedia page details for '${title}': ${summaryRes.statusText}`);
		throw new Error(`Failed to fetch Wikipedia page details: ${summaryRes.statusText}`);
	}

	const summaryData = (await summaryRes.json()) as WikiSummaryResponse;

	let imagesList: string[] = [];
	const seenFilenames = new Set<string>();

	if (summaryData.thumbnail?.source) {
		imagesList.push(setWikiUrlResolution(summaryData.thumbnail.source, imageSize));
		seenFilenames.add(getWikiFilename(summaryData.thumbnail.source));
	}

	if (mediaRes.ok) {
		const mediaData = (await mediaRes.json()) as WikiMediaListResponse;

		for (const item of mediaData.items || []) {
			if (imagesList.length >= maxImages) break;
			if (item.type !== 'image' || !item.srcset?.length) continue;

			const baseSrc = item.srcset[0].src;
			const absoluteSrc = baseSrc.startsWith('//') ? `https:${baseSrc}` : baseSrc;
			const imageUrl = setWikiUrlResolution(absoluteSrc, imageSize);
			const filename = getWikiFilename(imageUrl);

			if (seenFilenames.has(filename)) continue;

			seenFilenames.add(filename);
			imagesList.push(imageUrl);
		}
	}

	// If media-list fails, use the summary's default thumbnail scaled to the requested size.
	if (imagesList.length === 0 && summaryData.thumbnail?.source) {
		imagesList.push(setWikiUrlResolution(summaryData.thumbnail.source, imageSize));
	}

	return {
		title: summaryData.title,
		images: imagesList,
		description: summaryData.extract || null,
		subtitle: summaryData.description || null,
		wikidataId: summaryData.wikibase_item || null
	};
}

/**
 * Batches up to 20 article summaries, subtitles, and lead images into 1 single HTTP request
 */
export async function wikiGetBatchSummaries(
	titles: string[],
	{ imageSize = 500 }: { imageSize?: WikiImageSize } = {}
): Promise<Map<string, WikiBatchSummary>> {
	const resultMap = new Map<string, WikiBatchSummary>();
	if (titles.length === 0) return resultMap;

	// MediaWiki limits exlimit (extracts limit) to 20 for anonymous requests
	const BATCH_SIZE = 20;

	for (let i = 0; i < titles.length; i += BATCH_SIZE) {
		const batch = titles.slice(i, i + BATCH_SIZE);
		const titlesParam = encodeURIComponent(batch.join('|'));

		const url = `https://en.wikipedia.org/w/api.php?action=query&format=json&formatversion=2&origin=*&redirects=1&titles=${titlesParam}&prop=description|extracts|pageimages|pageviews|pageprops&ppprop=wikibase_item&exintro=1&explaintext=1&exlimit=max&piprop=thumbnail&pithumbsize=${imageSize}`;

		try {
			const res = await fetch(url);
			if (!res.ok) continue;

			const data = (await res.json()) as WikiBatchResponse & {
				query?: {
					normalized?: Array<{ from: string; to: string }>;
					redirects?: Array<{ from: string; to: string }>;
				};
			};

			const pages: WikiBatchPage[] = data.query?.pages ?? [];

			for (const page of pages) {
				if (page.missing) continue;

				const totalPageviews = Object.values(page.pageviews ?? {}).reduce(
					(sum: number, count) => sum + (count ?? 0),
					0
				);

				const summary: WikiBatchSummary = {
					title: page.title,
					description: page.extract || null,
					subtitle: page.description || null,
					thumbnail: page.thumbnail?.source || null,
					pageViews: totalPageviews,
					wikidataId: page.pageprops?.wikibase_item || null
				};

				// 1. Map by canonical title
				resultMap.set(page.title, summary);

				// 2. Map aliases so searching by original requested title always hits
				for (const red of data.query?.redirects ?? []) {
					if (red.to === page.title) {
						resultMap.set(red.from, summary);
					}
				}
				for (const norm of data.query?.normalized ?? []) {
					if (norm.to === page.title) {
						resultMap.set(norm.from, summary);
					}
				}
			}
		} catch (err) {
			console.warn('Batch summaries fetch failed:', err);
		}
	}

	return resultMap;
}

/**
 * Fetches only the media list (gallery photos) for a single page, avoiding redundant summary fetches
 */
export async function wikiGetPageMediaList(
	title: string,
	{ maxImages = 4, imageSize = 500 }: { maxImages?: number; imageSize?: WikiImageSize } = {}
): Promise<string[]> {
	try {
		const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/media-list/${encodeURIComponent(title)}`);
		if (!res.ok) return [];

		const mediaData = (await res.json()) as WikiMediaListResponse;
		return (mediaData.items || [])
			.filter((item) => item.type === 'image' && item.srcset?.length > 0)
			.slice(0, maxImages)
			.map((item) => {
				const baseSrc = item.srcset[0].src;
				const absoluteSrc = baseSrc.startsWith('//') ? `https:${baseSrc}` : baseSrc;
				return setWikiUrlResolution(absoluteSrc, imageSize);
			});
	} catch {
		return [];
	}
}

// Utils //

function getWikiFilename(url: string): string {
	try {
		const decoded = decodeURIComponent(url);
		const parts = decoded.split('/');
		let filename = parts[parts.length - 1];
		if (/^\d+px-/.test(filename) && parts.length > 1) {
			filename = parts[parts.length - 2];
		}
		return filename.toLowerCase();
	} catch {
		return url;
	}
}

// a bit overcomplicated, because some wiki filenames begin with a string like "294px", which this takes care of
export function setWikiUrlResolution(url: string, size: WikiImageSize): string {
	const lastSlash = url.lastIndexOf('/');
	const base = url.substring(0, lastSlash + 1);
	const filename = url.substring(lastSlash + 1);
	const newFilename = filename.replace(/^\d+px-/, `${size}px-`);
	return base + newFilename;
}

// Interfaces //

interface WikiGeosearchResult {
	pageid: number;
	title: string;
	lat: number;
	lon: number;
}

interface WikiGeosearchResponse {
	query?: {
		geosearch?: WikiGeosearchResult[];
	};
}

interface WikiImageResponse {
	query?: {
		pages?: Record<string, WikiImagePage>;
	};
}

interface WikiImagePage {
	thumbnail?: { source: string };
}

interface WikiSummaryResponse {
	title: string;
	displaytitle: string;
	description?: string;
	extract?: string;
	wikibase_item?: string;
	thumbnail?: {
		source: string;
		width: number;
		height: number;
	};
}

interface WikiMediaItem {
	title: string;
	type: 'image' | 'video' | 'audio' | string;
	srcset: Array<{ src: string; scale: string }>;
	showInGallery?: boolean;
}

interface WikiMediaListResponse {
	items?: WikiMediaItem[];
}

interface WikiItemDetails {
	title: string,
	images: string[],
	description: string | null,
	subtitle: string | null,
	wikidataId: string | null,
}

interface WikiBatchResponse {
	batchcomplete?: boolean;
	query?: {
		pages?: WikiBatchPage[];
	};
}

interface WikiBatchPage {
	pageid?: number;
	ns?: number;
	title: string;
	missing?: boolean;
	extract?: string;
	description?: string;
	pageviews?: Record<string, number | null>;
	pageprops?: {
		wikibase_item?: string;
	};
	thumbnail?: {
		source: string;
		width?: number;
		height?: number;
	};
}

export interface WikiBatchSummary {
	title: string;
	description: string | null;
	subtitle: string | null;
	thumbnail: string | null;
	pageViews: number;
	wikidataId: string | null;
}